import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { wp } from "../api/WpApi";
import PageLoading from "./PageLoading.js";
import data from "./_setup.json";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import DEFAULT_THUMBNAIL from "../../resources/not-found.png";

const SearchResultsPage = () => {
  const { query, page } = useParams();
  const currentpage = page ? parseInt(page, 10) : 1;
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalpages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    wp.posts()
      .search(query)
      .perPage(10)
      .page(currentpage)
      .then((posts) => {
        setResults(posts);
        setTotalPages(posts._paging ? posts._paging.totalPages : 1);
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err);
        setIsLoading(false);
      });
  }, [query, currentpage]);

  if (isLoading) return <PageLoading />;
  if (error) return <div className="content">
    <Helmet>
      <meta name="robots" content="noindex"></meta>
    </Helmet>
    Error: {error.message}</div>;

  return (
    <div className="content container">
      <Helmet>
        <title>Search results for "{query}" | Andrew's Cookin'</title>
        <meta
          name="description"
          content={`Browse all recipes matching "${query}". Find meals, snacks, desserts and more on Andrew's Cookin' recipe website.`}
        />
        <meta name="robots" content="noindex, follow" />
        <link
          rel="canonical"
          href={`${
            process.env.REACT_APP_PUBLIC_URL
          }/search/${encodeURIComponent(query)}/${
            currentpage > 1 ? `page-${currentpage}` : ""
          }`}
        />

      <script type="application/ld+json">
      {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `Search results for "${query}"`,
        itemListElement: results.map((post, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": post.type === "recipe" ? "Recipe" : "Article",
            name: post.title.rendered.replace(/<[^>]+>/g, ""),
            url: `${process.env.REACT_APP_PUBLIC_URL}/${data.url.slug_posts}/${post.slug}/`,
            image:
              post.yoast_head_json?.og_image?.[0]?.url || DEFAULT_THUMBNAIL
          }
        }))
      })}
      </script>

      </Helmet>
      <h1 className="mt-4 fw-bold">
        Search Results for: <strong>"{query}"</strong>
      </h1>
      <hr />
      {results.length === 0 ? (
        <p>No results found.</p>
      ) : (
        <div className="list-group">
          {results.map((post) => (
            <Link
              key={post.id}
              to={`/${data.url.slug_posts}/${post.slug}/`}
              className="shadow-sm list-group-item list-group-item-action transition ease-in-out delay-50 duration-300 "
            >
              {" "}
              <div className="grid grid-flow-row-dense md:grid-cols-5 md:justify-items-between justify-items-center">
                <div className="md:col-span-1">
                  {
                    <div className="post-thumbnail my-3">
                      <img
                        src={
                          post.yoast_head_json.og_image !== undefined &&
                          post.yoast_head_json
                            ? post.yoast_head_json.og_image[0].url
                            : DEFAULT_THUMBNAIL
                        }
                        alt={post.title.rendered}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = DEFAULT_THUMBNAIL;
                        }}
                      ></img>
                    </div>
                  }
                </div>
                <div className="md:col-span-4">
                  <h2
                    dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                  />
                  <p
                    dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }}
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      {totalpages > 1 && (
        <div className="row">
          <div className="col d-flex justify-content-center align-items-center my-5">
            <nav>
              <ul className="pagination text-color">
                <li
                  className={`page-item ${currentpage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      navigate(
                        currentpage === 2
                          ? `/search/${encodeURIComponent(query)}/`
                          : `/search/${encodeURIComponent(query)}/page-${
                              currentpage - 1
                            }`
                      )
                    }
                    disabled={currentpage === 1}
                  >
                    <span aria-hidden="true">&laquo;</span>
                    <span className="sr-only">Previous</span>
                  </button>
                </li>
                {Array.from({ length: totalpages }, (_, i) => (
                  <li
                    key={i}
                    className={`page-item ${
                      currentpage === i + 1 ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() =>
                        navigate(
                          i === 0
                            ? `/search/${encodeURIComponent(query)}/`
                            : `/search/${encodeURIComponent(query)}/page-${
                                i + 1
                              }`
                        )
                      }
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentpage === totalpages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      navigate(
                        `/search/${encodeURIComponent(query)}/page-${
                          currentpage + 1
                        }`
                      )
                    }
                    disabled={currentpage === totalpages}
                  >
                    <span aria-hidden="true">&raquo;</span>
                    <span className="sr-only">Next</span>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResultsPage;
